import {MinMaxRateValue} from './component/min-max-rate-component.domain';
import {AssetCategoryRateValue} from './component/asset-category-rate-component';
import {AssetTypeRateValue} from './component/asset-type-rate-component.domain';

import {Operator} from '../app-calculator';
import {SortTypes} from './types.domain';
import {OriginatorRelationshipValue} from './component/originator-relationship-component.domain';
import {TransactionTypeRateValue} from './component/transaction-type-rate-component.domain';
import {SecurityTypeRateValue} from "./component/security-type-rate-component.domain";

export type RatecardListSortType = SortTypes<'RatecardId' | 'Name' | 'CreateTime'>;
export type OriginatorListSortType = SortTypes<'OriginatorBusinessId' | 'EntityName' | 'ABN' | 'Type' | 'SalesforceId'>;
export type AccreditationListingSortType = SortTypes<'companyName' | 'applicantNumber'>
export interface Ratecard {
  RatecardId: number,
  Name: string,
  CreateTime: string,
}

export type RatecardList = Ratecard;


export interface UpdateOriginatorInput {
  // Type?: OriginatorType,
  Relationship: OriginatorRelationshipValue,
  EntityName: string,
  EntityType: string,
  ABN: string,
  ACN: string,
  // Aggregator?: number,
  Website: string,
  Email: string,
  TelephoneNumber: string,
  RatecardId?: number,
  PercentagePaid: number,
  whitelabelId?: number,
}

export interface UpdateAggregatorRemittanceEmailInput {
  originatorBusinessId: number,
  remittanceEmail: string | null,
}

// ZEN-XXX: AF Tier 1 & Tier 2 rate cards collapsed into a unified 4-bucket
// structure: one `New` rate (used when AssetCondition == 'New') and three
// editable `Used` age buckets. Y1/Y2 thresholds stay editable per rate card.
export interface AssetFinanceTier1Range {
  New: { Rate: string };
  YearRanges: {
    YearRange: string;
    Operator: Operator;
    Threshold: string;
    YearRangeType: 'Low' | 'Medium' | 'High';
    Rate: string;
  }[];
}

export interface AssetFinanceTier2Range {
  New: { Rate: string };
  YearRanges: {
    YearRange: string;
    Operator: Operator;
    Threshold: string;
    YearRangeType: 'Low' | 'Medium' | 'High';
    Rate: string;
  }[];
}

export interface AssetFinanceTier1 {
  YearRangeThresholds: string[];
  AssetBacked: AssetFinanceTier1Range;
}

export interface AssetFinanceTier2 {
  YearRangeThresholds: string[];
  AssetBacked: AssetFinanceTier2Range;
}

// 5.7.0: AF Tier 3 collapsed into the same 4-bucket structure as Tier 1/2 —
// one `New` rate plus three editable `Used` age buckets bounded by the Year1/
// Year2 thresholds. The previous Solar-specific matrix has been removed; Solar
// (asset category 106) now prices off this same AssetBacked matrix.
export interface AssetFinanceTier3Range {
  New: { Rate: string };
  YearRanges: {
    YearRange: string;
    Operator: Operator;
    Threshold: string;
    YearRangeType: 'Low' | 'Medium' | 'High';
    Rate: string;
  }[];
}

export interface AssetFinanceTier3 {
  YearRangeThresholds: string[];
  AssetBacked: AssetFinanceTier3Range;
}

export interface AssetFinanceTier4 {
  AssetBacked: {Type: 'New' | 'Used', Rate: string}[];
}


export interface UpdateRatecardInput {
  Name: string,  // rate card name
  AssetFinance: { ApplicationType: 'AssetFinance' } & RateCardInputData,
  BusinessLoans: {ApplicationType: 'BusinessLoans' } & RateCardInputData,
  BusinessOverdraft: {ApplicationType: 'BusinessOverdraft' } & RateCardInputData,
  InsurancePremium: { ApplicationType: 'InsurancePremium' } & RateCardInputData,
}

export interface RateCardInputData {
  BaseInterestRate?: number,
  LoanAmount?: MinMaxRateValue,
  AssetCategory?: AssetCategoryRateValue,
  AssetType?: AssetTypeRateValue,
  EOTAge?: MinMaxRateValue,
  LoanTerms?: MinMaxRateValue,
  Brokerage?: MinMaxRateValue,
  AdversOnFile?: number,
  CarsPropertyOwner?: number,
  NoPropertyOwnership?: number,
  CapRate?: number,
  Truck12T?: number,
  NoAssetBacked?: number,
  TransactionTypeRate?: TransactionTypeRateValue,
  PrivateSaleDocFee?: number,
  PrivateSale?: number,
  DocFee?: MinMaxRateValue,
  AssetConditionUsed?: number,
  LowDirectorScoreUplift?: number,
  LowDepositUplift?: number,
  PremiumDiscount?: number,
  ABNorGSTAge?: MinMaxRateValue,
  AssetFinanceTier1?: AssetFinanceTier1 | null,
  AssetFinanceTier2?: AssetFinanceTier2 | null,
  AssetFinanceTier3?: AssetFinanceTier3 | null,
  AssetFinanceTier4?: AssetFinanceTier4 | null,
  PropertyOwner?:number;
  NoPropertyOwner?:number;
  NoPropertyOwnerUplift?: number;
  LowScoreUplift?: number;
  FacilityFee?: number;
  BrokerShareFacilityFee?: number;
  DirectorScoreRate?: number;
  CarsAndCaravans?: MinMaxRateValue;
  Motorbikes?: MinMaxRateValue;
  SolarSystems?: MinMaxRateValue;
  Hybrid?: number;
  NonPropertyOwnerBetween500And600Rate?: number;
  SecurityType?: SecurityTypeRateValue;
  MonthlyAccountKeepingFee?: number;
  MaxBrokerRateDiscount?: number;
  CreditAssitanceFee?: number;
  PpsrFee?: number;
  MaxFacilityEstablishmentFee?: number;
  // ZEN-818 follow-up: 3-tier pricing for BL/OD rate cards.
  Tiers?: { tier1: number; tier2: number; tier3: number } | null;
}

export interface UpdateRatecardResult {
  ratecardId: number,
  ratecardDetailIds: number[],
}

